import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import StyledSelect from './StyledSelect';
import Button from './ui/Button';
import Card from './ui/Card';
import Input from './ui/Input';

const supportFormCard = {
  maxWidth: '100%',
  padding: 18,
  borderRadius: 12,
  background: 'rgba(10, 14, 39, 0.6)',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  boxShadow: 'none',
};

const supportSubjectInput = {
  padding: '10px 12px',
  background: 'rgba(10, 14, 39, 0.8)',
  color: '#E2E8F0',
};

const SupportTickets = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('geral');
  const [priority, setPriority] = useState('media');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/support/tickets`, { params: { limit: 20 } });
      setTickets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao carregar tickets.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', text: '' });
    setSending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/support/tickets`, {
        subject,
        message,
        category,
        priority
      });
      const ticketId = res.data?.ticket?.ticketId || 'n/a';
      setFeedback({ type: 'success', text: `Ticket ${ticketId} criado com sucesso. Nossa equipe recebeu no e-mail de suporte.` });
      setSubject('');
      setMessage('');
      setCategory('geral');
      setPriority('media');
      await loadTickets();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao abrir ticket de suporte.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Suporte</h2>
      <p style={styles.subtitle}>
        Abra um ticket e nossa equipe recebe automaticamente no e-mail.
      </p>

      {feedback.text && (
        <div style={{
          ...styles.message,
          ...(feedback.type === 'success' ? styles.success : styles.error)
        }}>
          {feedback.text}
        </div>
      )}

      <Card style={supportFormCard}>
        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Assunto</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex.: Dúvida na instalação do widget"
            style={supportSubjectInput}
            required
            minLength={5}
          />

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Categoria</label>
              <StyledSelect
                value={category}
                onChange={setCategory}
                options={[
                  { value: 'geral', label: 'Geral' },
                  { value: 'integracao', label: 'Integração' },
                  { value: 'bug', label: 'Bug' },
                  { value: 'billing', label: 'Faturamento' },
                  { value: 'treinamento', label: 'Treinamento' }
                ]}
                placeholder="Categoria"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Prioridade</label>
              <StyledSelect
                value={priority}
                onChange={setPriority}
                options={[
                  { value: 'baixa', label: 'Baixa' },
                  { value: 'media', label: 'Média' },
                  { value: 'alta', label: 'Alta' }
                ]}
                placeholder="Prioridade"
              />
            </div>
          </div>

          <label style={styles.label}>Mensagem</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Descreva o problema e, se possível, inclua passos para reproduzir."
            rows={6}
            style={styles.textarea}
            required
            minLength={10}
          />

          <Button type="submit" disabled={sending} style={{ marginTop: 14 }}>
            {sending ? 'Enviando...' : 'Abrir ticket'}
          </Button>
        </form>
      </Card>

      <div style={styles.history}>
        <h3 style={styles.historyTitle}>Meus tickets recentes</h3>
        {loading && <p style={styles.loading}>Carregando...</p>}
        {!loading && tickets.length === 0 && <p style={styles.empty}>Nenhum ticket aberto ainda.</p>}
        {!loading && tickets.length > 0 && tickets.map((t) => (
          <div key={t.ticketId} style={styles.ticketCard}>
            <div style={styles.ticketHeader}>
              <strong>{t.subject}</strong>
              <span style={styles.badge}>{t.status}</span>
            </div>
            <div style={styles.ticketMeta}>
              #{t.ticketId} · {t.category} · prioridade {t.priority} · {t.createdAt ? new Date(t.createdAt).toLocaleString('pt-BR') : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Vídeo de ajuda - Suporte */}
      <section style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0, 212, 255, 0.2)' }}>
        <h3 style={{ color: '#00D4FF', fontSize: '1rem', fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Vídeo de ajuda</h3>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: 800, margin: '0 auto', borderRadius: 12, border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <iframe
            title="Como usar o Suporte"
            src="https://www.loom.com/embed/07bfb4c51e6a4cc7a7cdcf62b6947c73"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            frameBorder="0"
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
};

const styles = {
  container: { padding: 24, maxWidth: 900, margin: '0 auto' },
  title: { color: '#fff', marginBottom: 8, fontSize: '1.8rem' },
  subtitle: { color: '#B0BEC5', marginBottom: 18, fontSize: 14 },
  label: {
    display: 'block',
    marginBottom: 8,
    marginTop: 10,
    color: '#B0BEC5',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid rgba(0, 212, 255, 0.3)',
    background: 'rgba(10, 14, 39, 0.8)',
    color: '#E2E8F0',
    resize: 'vertical'
  },
  row: { display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 220 },
  message: { padding: 12, borderRadius: 8, marginBottom: 12, fontWeight: 600 },
  success: { background: 'rgba(0, 212, 255, 0.2)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.4)' },
  error: { background: 'rgba(255, 107, 53, 0.2)', color: '#FF6B35', border: '1px solid rgba(255, 107, 53, 0.4)' },
  history: { marginTop: 20, padding: 18, borderRadius: 12, border: '1px solid rgba(0, 212, 255, 0.2)', background: 'rgba(10, 14, 39, 0.6)' },
  historyTitle: { color: '#E2E8F0', marginBottom: 12 },
  loading: { color: '#B0BEC5' },
  empty: { color: '#B0BEC5' },
  ticketCard: { padding: 12, borderRadius: 8, border: '1px solid rgba(0, 212, 255, 0.15)', background: 'rgba(0,0,0,0.2)', marginBottom: 10 },
  ticketHeader: { display: 'flex', justifyContent: 'space-between', gap: 10, color: '#E2E8F0' },
  badge: { fontSize: 12, color: '#00D4FF', textTransform: 'uppercase' },
  ticketMeta: { marginTop: 6, fontSize: 12, color: '#B0BEC5' }
};

export default SupportTickets;
